<script setup lang="ts">
import { PhHouse } from '@phosphor-icons/vue';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DropZone } from '@/components/document/sub-components/file-manager/sub-components/drop-zone';
import { useFileManagerNavigation } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerNavigation';
import { PathSegments } from './sub-components/path-segments';

const props = defineProps<{
  path: string;
}>();

const { navigateTo } = useFileManagerNavigation();
const { t } = useI18n();

const pathBar = ref<HTMLDivElement>();

function scrollToEnd() {
  if (pathBar.value) {
    pathBar.value.scrollLeft = pathBar.value.scrollWidth;
  }
}

onMounted(scrollToEnd);
watch(
  () => props.path,
  () => nextTick(scrollToEnd),
);
</script>

<template>
  <div class="path-bar">
    <div ref="pathBar" class="path-bar-scroll">
      <div class="path-bar-holder path-bar-holder-start">
        <DropZone
          path="/"
          v-slot="{ isDragOver, onDragEnter, onDragLeave, onDragOver, onDrop }"
        >
          <button
            type="button"
            class="button is-small path-bar-segment"
            :class="{ 'is-drag-over': isDragOver }"
            :aria-label="t('fileManager.header.pathBar.goToRootAriaLabel')"
            @click="navigateTo('/')"
            @dragenter.prevent="onDragEnter"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
          >
            <PhHouse :size="22" weight="fill" />
          </button>
        </DropZone>
      </div>
      <PathSegments :path="path" />
    </div>
  </div>
</template>

<style scoped>
.path-bar {
  flex: 1;
  overflow: hidden;
  border-radius: 0.75rem;
  background-color: #fff;
}

.path-bar-scroll {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.3rem 0;
  color: #001c74;
  font-size: 1.0625rem;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.path-bar-scroll::-webkit-scrollbar {
  display: none;
}

.path-bar-segment {
  flex-shrink: 0;
  height: 2.625rem;
  padding: 0 0.6em;
  border: none;
  border-radius: 0.375rem;
  background-color: transparent;
  box-shadow: none;
  color: #001c74;
  font-size: 1.0625rem;
  line-height: 2.625rem;
}

.path-bar-segment:hover {
  background-color: #e6ecfb;
}

.path-bar-segment.is-drag-over {
  background-color: color-mix(in srgb, var(--primary-color) 10%, transparent);
}

.path-bar-holder {
  position: sticky;
  z-index: 1;
  display: flex;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  background-color: #fff;
}

.path-bar-holder-start {
  left: 0;
  padding-left: 0.75rem;
}

.path-bar-holder-start::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  width: 1rem;
  background: linear-gradient(to right, #fff, transparent);
  pointer-events: none;
}
</style>
